import React, {useCallback, useEffect} from "react";
import {useForm} from "src/core/common/components/FormContext";
import curry from "lodash/curry";
import validateCard from "card-validator";
import {Field} from "react-final-form";
import {
  composeValidators,
  required,
  validateCardHolderName,
  validateCreditCardCvc,
  validateCreditCardExpirationDate,
  validateCreditCardNumber,
  validateCreditCardPostalCode,
} from "src/core/common/validations";
import Error from "src/core/common/components/elements/Error";
import {
  formatCreditCardNumber,
  formatCVC,
  formatExpirationDate,
} from "src/core/payments/merrco/formatCardInfo";
import PaymentIcon from "react-payment-icons-inline";
import Input from "src/core/common/components/elements/input/Input";
import styled from "styled-components";
import {FieldContainer} from "src/core/common/components/collections/form/forms";
import {cardTypes} from "src/core/payments/CardComponents/cardTypes";
import {useShop} from "src/core/shops";

function NewCardForm({setLoading, paymentOption, form, disabled}) {
  const isMerrcoSetup = Boolean(window.Merrco);

  const [, shop] = useShop();
  const shopCountry = shop.getShop()?.getCountry();

  const {requestDisabled} = useForm();

  const startAsyncRequest = useCallback(() => {
    setLoading(true);
    requestDisabled(true);
  }, [requestDisabled, setLoading]);

  const finishAsyncRequest = useCallback(() => {
    setLoading(false);
    requestDisabled(false);
  }, [requestDisabled, setLoading]);

  /* eslint-disable */
  useEffect(() => {
    if (!isMerrcoSetup) {
      startAsyncRequest();
      paymentOption.setupMerrco();
      finishAsyncRequest();
    }
  }, [isMerrcoSetup]);

  const onChangeCardNumber = curry((callback, evt) => {
    const cardNumber = evt.target.value;
    updatePaymentSourceCard("cardNumber", cardNumber.replace(/ /g, ""));
    callback(cardNumber);
  });

  const onChangeCardCvc = curry((callback, evt) => {
    const cvc = evt.target.value.slice(0, 4);

    updatePaymentSourceCard("cardCvc", cvc);
    callback(cvc);
  });

  const onChangeCardDate = curry((callback, evt) => {
    const date = evt.target.value.slice(0, 5);
    const splitDate = date.split("/");

    updatePaymentSourceCard("cardMonth", splitDate[0]);
    updatePaymentSourceCard("cardYear", splitDate[1]);
    callback(date);
  });

  const onChangeCardPostalCode = curry((callback, evt) => {
    const postalCode = evt.target.value;
    updatePaymentSourceCard("cardPostalCode", postalCode);
    callback(postalCode);
  });

  const updatePaymentSourceCard = (cardFieldName, value) => {
    form.mutators.setValue("payment.source." + cardFieldName, value);
  };

  const getErrorMessage = meta => {
    if (!meta.touched || !meta.error) return null;

    let msg;
    if (typeof meta.error === "string") {
      msg = meta.error;
    } else if (typeof meta.error === "object") {
      if (meta.active && meta.error.validation?.isPotentiallyValid) {
        return null;
      }
      msg = meta.error.message;
    } else {
      msg = "Invalid";
    }
    return msg;
  };

  const getCardType = value => {
    const validation = validateCard.number(value);
    const type = validation?.card?.type || "";
    return cardTypes[type];
  };

  return (
    <div>
      <div>
        <Field
          validate={composeValidators(required, validateCreditCardNumber)}
          name="creditCardNumber"
          type="text"
          pattern="[\d| ]{16,22}"
          format={formatCreditCardNumber}
          render={({input, meta}) => (
            <CustomFieldContainer>
              <CardInput
                {...input}
                disabled={disabled}
                hasErrors={getErrorMessage(meta)}
                placeholder={"Card Number"}
                onChange={onChangeCardNumber(input.onChange)}
              />
              {getErrorMessage(meta) && <Error>{getErrorMessage(meta)}</Error>}
              <CardIconContainer>
                {getCardType(input.value) && (
                  <PaymentIcon
                    key={getCardType(input.value)}
                    icon={getCardType(input.value)}
                    style={{width: 50, height: 32}}
                  />
                )}
              </CardIconContainer>
            </CustomFieldContainer>
          )}
        />
      </div>
      <div>
        <Field
          validate={composeValidators(required, validateCardHolderName)}
          name="creditCardHolderName"
          type="text"
          render={({input, meta}) => (
            <CustomFieldContainer>
              <CardInput
                {...input}
                hasErrors={getErrorMessage(meta)}
                disabled={disabled}
                placeholder={"Name"}
              />
              {getErrorMessage(meta) && <Error>{getErrorMessage(meta)}</Error>}
            </CustomFieldContainer>
          )}
        />
      </div>
      <SmallContainer>
        <Field
          validate={composeValidators(required, validateCreditCardExpirationDate)}
          name="creditCardExpirationDate"
          type="text"
          pattern="\d\d/\d\d"
          format={formatExpirationDate}
          render={({input, meta}) => (
            <CustomFieldContainer>
              <SmallCardInput
                {...input}
                disabled={disabled}
                hasErrors={getErrorMessage(meta)}
                placeholder={"Expiration Date"}
                onChange={onChangeCardDate(input.onChange)}
              />
              {getErrorMessage(meta) && <Error>{getErrorMessage(meta)}</Error>}
            </CustomFieldContainer>
          )}
        />
        <CVC>
          <Field
            validate={composeValidators(required, validateCreditCardCvc)}
            name="creditCardCVC"
            type="text"
            pattern="\d{3,4}"
            format={formatCVC}
            render={({input, meta}) => (
              <CustomFieldContainer>
                <Input
                  {...input}
                  placeholder={"CVC"}
                  hasErrors={getErrorMessage(meta)}
                  disabled={disabled}
                  onChange={onChangeCardCvc(input.onChange)}
                />
                {getErrorMessage(meta) && <Error>{getErrorMessage(meta)}</Error>}
              </CustomFieldContainer>
            )}
          />
        </CVC>
        <Field
          validate={composeValidators(required, value =>
            validateCreditCardPostalCode(value, shopCountry)
          )}
          name="creditCardPostalCode"
          type="text"
          render={({input, meta}) => (
            <CustomFieldContainer>
              <CardInput
                {...input}
                placeholder="Postal Code"
                hasErrors={getErrorMessage(meta)}
                disabled={disabled}
                onChange={onChangeCardPostalCode(input.onChange)}
              />
              {getErrorMessage(meta) && <Error>{getErrorMessage(meta)}</Error>}
            </CustomFieldContainer>
          )}
        />
      </SmallContainer>
    </div>
  );
}

const SmallContainer = styled.div`
  display: flex;
  column-gap: ${({theme}) => theme.v2.spacing(2)};
`;

const CardInput = styled(Input)`
  width: 100%;
`;

const SmallCardInput = styled(Input)`
  width: 100%;
`;

const CustomFieldContainer = styled(FieldContainer)`
  margin-bottom: ${({theme}) => theme.v2.spacing(2)};
`;

const CardIconContainer = styled.div`
  margin: 0;
  position: absolute;
  right: 8px;
  top: 27px;
  transform: translate3d(0, -50%, 0);
  width: auto !important;
`;

const CVC = styled.div`
  max-width: 20%;
`;

export default NewCardForm;
